import { gql, useMutation } from '@apollo/client'
import { ethers } from 'ethers'

import { config } from '@/config'
import { useTranslation } from '@/core'
import { i18n } from '@/core/i18n/init'
import { getPlatform } from '@/core/native/app'

import { networks } from '../network'

/**
 * Metamask clientがインストール済みかどうか
 */
export const checkMetamaskInstalled = () => {
  if (isMetamaskInstalled()) return true

  /**
   * PCブラウザ:
   * metamaskクライアントがない場合、取得を促す
   */
  const platform = getPlatform()
  if (
    platform === 'web' &&
    !navigator.userAgent.match(/iPhone|Android.+Mobile/)
  ) {
    window.open('https://metamask.io/download/')
    throw new Error(i18n.t('confirmMetamaskClient', { ns: 'wallet' }))
  }

  /**
   * native or nativeブラウザ:
   * - MetamaskMobileアプリインストール済： MetamaskMobileアプリ内で開く
   * - MetamaskMobile未インストール： MetamaskMobileのストアを開く
   */
  window.open(
    `https://metamask.app.link/dapp/${config.domain}/login?redirectUri=${location.pathname}`
  )

  throw new Error(i18n.t('requestMetamaskMobile', { ns: 'wallet' }))
}

export const isMetamaskInstalled = () => {
  return !!window?.ethereum?.isMetaMask
}

/**
 * Metamask clientとの接続
 * @return 接続済みのウォレットのpublic address
 */
export const connectMetamaskClient = async () => {
  checkMetamaskInstalled()

  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const accounts = await provider.send('eth_requestAccounts', [])

  return accounts[0]
}

/**
 * メッセージによるウォレットクライアントでの認証
 */
export const signWalletBySignatureMessage = async (
  signer: ethers.Signer,
  signatureMessage: string
) => {
  return await signer.signMessage(signatureMessage)
}

/**
 * アカウントの残高を取得
 */
export const getBalance = async (walletAddress: string) => {
  /**
   * @todo
   * polygon or mumbai以外導入の際には他通貨分も拡張する
   */
  const provider = new ethers.providers.JsonRpcProvider(
    networks[
      config.environment === 'production' ? 'polygon' : 'mumbai'
    ].rpcUrls[0]
  )

  const balance = await provider.getBalance(walletAddress)

  return ethers.utils.formatEther(balance)
}

export const useMetamask = () => {
  const { t } = useTranslation('wallet')

  /**
   * user用ウォレット連携認証メッセージ発行・ウォレット連携
   */
  const [generateWalletSignatureMessage] = useMutation(
    GENERATE_WALLET_SIGNATURE_MESSAGE
  )
  const [authorizeWallet] = useMutation(AUTHORIZE_WALLET)

  /**
   * artist用ウォレット連携認証メッセージ発行・ウォレット連携
   */
  const [artistGenerateWalletSignatureMessage] = useMutation(
    ARTIST_GENERATE_WALLET_SIGNATURE_MESSAGE
  )
  const [artistAuthorizeWallet] = useMutation(ARTIST_AUTHORIZE_WALLET)

  /**
   * 認証メッセージによるウォレット認証・firestore userDocへのウォレットアドレス登録
   * @return true: 完了 error: 各種エラー
   */
  const userAuthorizeWalletBySignatureMessage = async () => {
    checkMetamaskInstalled()

    if (!window.ethereum) throw new Error(t('confirmMetamaskClient'))

    const provider = new ethers.providers.Web3Provider(window.ethereum)
    const signer = provider.getSigner()

    /**
     * ウォレットクライアントとの認証（アカウント取得）
     */
    const account = await connectMetamaskClient()

    /**
     * 署名用メッセージ取得mutation（ユーザー用）
     */
    const { data } = await generateWalletSignatureMessage()
    const signatureMessage =
      data?.generateWalletSignatureMessage?.authToken?.signatureMessage

    if (!signatureMessage) throw new Error(t('signatureError'))

    /**
     * 署名用メッセージにて署名
     */
    const signature = await signWalletBySignatureMessage(
      signer,
      signatureMessage
    )

    /**
     * バックエンド側にてアドレス登録（ユーザー用）
     */
    await authorizeWallet({
      variables: { inputs: { publicAddress: account, signature } },
    })

    return true
  }

  /**
   * 認証メッセージによるウォレット認証・firestore artistDocへのウォレットアドレス登録
   * @return true: 完了 error: 各種エラー
   */
  const artistAuthorizeWalletBySignatureMessage = async () => {
    checkMetamaskInstalled()

    if (!window.ethereum) throw new Error(t('confirmMetamaskClient'))

    const provider = new ethers.providers.Web3Provider(window.ethereum)
    const signer = provider.getSigner()

    /**
     * ウォレットクライアントとの認証（アカウント取得）
     */
    const account = await connectMetamaskClient()

    /**
     * 署名用メッセージ取得mutation（アーティスト用）
     */
    const { data } = await artistGenerateWalletSignatureMessage()

    const signatureMessage =
      data?.artistGenerateWalletSignatureMessage?.authToken?.signatureMessage

    if (!signatureMessage) throw new Error(t('signatureError'))

    /**
     * 署名用メッセージにて署名
     */
    const signature = await signWalletBySignatureMessage(
      signer,
      signatureMessage
    )

    /**
     * バックエンド側にてアドレス登録（アーティスト用）
     */
    await artistAuthorizeWallet({
      variables: { inputs: { publicAddress: account, signature } },
    })

    return true
  }

  return {
    userAuthorizeWalletBySignatureMessage,
    artistAuthorizeWalletBySignatureMessage,
  }
}

export async function checkChain(chainName: string) {
  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const signer = provider.getSigner()

  const networkName =
    {
      polygon: 'matic',
    }[chainName] || chainName

  const chainId = await signer.getChainId()
  return ethers.providers.getNetwork(chainId).name === networkName
}

export async function switchChain(chainName: keyof typeof networks) {
  window.ethereum.request({
    method: 'wallet_addEthereumChain',
    params: [networks[chainName]],
  })
}

const GENERATE_WALLET_SIGNATURE_MESSAGE = gql`
  mutation GenerateWalletSignatureMessage {
    generateWalletSignatureMessage(inputs: { cryptoWalletType: metamask }) {
      authToken {
        signatureMessage
      }
    }
  }
`

const ARTIST_GENERATE_WALLET_SIGNATURE_MESSAGE = gql`
  mutation GenerateWalletSignatureMessage {
    artistGenerateWalletSignatureMessage(
      inputs: { cryptoWalletType: metamask }
    ) {
      authToken {
        signatureMessage
      }
    }
  }
`

const AUTHORIZE_WALLET = gql`
  mutation AuthorizeWallet($inputs: AuthorizeWalletInput!) {
    authorizeWallet(inputs: $inputs) {
      address
      authToken {
        nonce
        used
        createdAt
        expiredAt
      }
      connectedAt
      status
      walletType
    }
  }
`

const ARTIST_AUTHORIZE_WALLET = gql`
  mutation AuthorizeWallet($inputs: AuthorizeWalletInput!) {
    artistAuthorizeWallet(inputs: $inputs) {
      address
      authToken {
        nonce
        used
        createdAt
        expiredAt
      }
      connectedAt
      status
      walletType
    }
  }
`
